import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "motion/react";
import clsx from "clsx";
import { Button } from "../../components/Button";
import { XMarkIcon, PencilIcon } from "@heroicons/react/24/outline";
import { APIs } from "../../services/Apis";
import { MultiStepLoaderBusinessDetails } from "../../components/loaders/MultiStepLoaderBusinessDetails";
import { OnboardingGlow } from "../../components/backgrounds/OnboardingGlow";
import {
  parseTone,
  extractDomain,
} from "../../components/settings/BrandProfileCard";

// ─── Shared field styling ────────────────────────────────────────────────────
//
// The forms plugin runs with strategy:"class", so bare inputs come through
// unstyled and these classes are the whole look.

const fieldClass =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm leading-relaxed text-gray-900 outline-none placeholder:text-gray-400 focus:border-primary focus:ring-[3px] focus:ring-primary/15";

const labelClass =
  "text-[10px] font-medium uppercase tracking-[0.08em] text-gray-400";

const TONE_SUGGESTIONS = [
  "Playful",
  "Technical",
  "Formal",
  "Bold",
  "Direct",
  "Warm",
  "Confident",
  "No jargon",
];

const AVATAR_TONES = [
  "bg-indigo-50 text-indigo-700",
  "bg-emerald-50 text-emerald-700",
  "bg-amber-50 text-amber-700",
  "bg-sky-50 text-sky-700",
  "bg-rose-50 text-rose-700",
];

const initials = (name) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("") || "?";

// The URL rows show the path in full weight and the host muted, so a list of
// pages from one site reads as a list of pages rather than a list of domains.
const splitUrl = (value) => {
  try {
    const parsed = new URL(
      /^https?:\/\//i.test(value) ? value : `https://${value}`,
    );
    return { host: parsed.host, path: parsed.pathname.replace(/\/$/, "") };
  } catch {
    return { host: value, path: "" };
  }
};

// ─── Row primitives ──────────────────────────────────────────────────────────

const Empty = ({ children }) => (
  <span className="text-sm text-gray-300">{children}</span>
);

const RowLabel = ({ children }) => (
  <div className="text-[13px] font-semibold text-gray-900">{children}</div>
);

const EditorActions = ({ onSave, onCancel, hint }) => (
  <div className="mt-3.5 flex items-center gap-2">
    <Button type="button" variant="solid" color="primary" size="sm" onClick={onSave}>
      Save ↵
    </Button>
    <Button type="button" variant="text" color="slate" size="sm" onClick={onCancel}>
      Cancel
    </Button>
    {hint && (
      <span className="flex-1 text-right text-[10.5px] text-gray-400">
        {hint}
      </span>
    )}
  </div>
);

// A dashed "add another" affordance, used by the list editors.
const AddRow = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full cursor-pointer rounded-lg border border-dashed border-gray-300 bg-white px-3 py-2 text-left text-sm text-gray-400 hover:border-gray-400 hover:text-gray-600"
  >
    {children}
  </button>
);

// ─── Main Component ──────────────────────────────────────────────────────────

export default function BusinessDetailsStep({
  url,
  onDataChange,
  onLoadingChange,
  onEditingChange,
}) {
  const [formData, setFormData] = useState({
    businessName: "",
    websiteUrl: url || "",
    businessDescription: "",
    companyLogo: "",
    companyColors: [],
    companyTone: "",
    stakeholders: [{ stakeholderId: 1, name: "", description: "" }],
    associatedUrls: [{ urlId: 1, url: "", summary: "" }],
  });
  const [loading, setLoading] = useState(true);

  // Which row is open for editing, and the uncommitted copy it edits against.
  // Nothing reaches formData — or the parent — until Save.
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [newTone, setNewTone] = useState("");

  // Notify parent when loading state changes
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(loading);
    }
  }, [loading, onLoadingChange]);

  // The footer CTA is disabled while a row is open, so the parent has to know.
  useEffect(() => {
    if (onEditingChange) {
      onEditingChange(editing !== null);
    }
  }, [editing, onEditingChange]);

  const [error, setError] = useState(null);
  const [prelimData, setPrelimData] = useState(null);
  const prelimFetchStartedRef = useRef(false);
  const businessFetchStartedRef = useRef(false);
  const dataReadyRef = useRef(false);
  const loaderTimerDoneRef = useRef(false);

  // Loader closes only when BOTH the minimum timer has elapsed AND the API call has returned
  const handleLoaderComplete = useCallback((value) => {
    if (value === false) {
      loaderTimerDoneRef.current = true;
      if (dataReadyRef.current) {
        setLoading(false);
      }
    } else {
      setLoading(value);
    }
  }, []);

  // Fetch business details when URL is provided
  useEffect(() => {
    const fetchPrelimBusinessDetails = async () => {
      if (!url || prelimFetchStartedRef.current) {
        return;
      }
      prelimFetchStartedRef.current = true;
      // First, call getPrelimBusinessDetails to get logo, colors, etc.
      const prelimData = await APIs.getPrelimBusinessDetails(url);
      try {
        setPrelimData({
          companyName: prelimData.companyName,
          companyLogo: prelimData.companyLogo,
          companyDescription: prelimData.companyDescription,
          companyTone: prelimData.companyTone,
          companyColors: prelimData.companyColors,
        });
      } catch (err) {
        console.error("Failed to fetch preliminary business details:", err);
      } finally {
        prelimFetchStartedRef.current = false;
      }
    };
    const fetchBusinessDetails = async () => {
      if (!url || businessFetchStartedRef.current) {
        return;
      }

      businessFetchStartedRef.current = true;

      setError(null);

      try {
        // Then call the main business details API
        const data = await APIs.getBusinessDetails(url);

        // Transform stakeholders to include IDs
        const stakeholdersWithIds = data.stakeholders.map(
          (stakeholder, index) => ({
            stakeholderId: stakeholder.stakeholderId || index + 1,
            name: stakeholder.name,
            description: stakeholder.description,
          }),
        );

        // Transform associatedUrls to include IDs
        const associatedUrlsWithIds = (data.associatedUrls || []).map(
          (associatedUrl, index) => ({
            urlId: index + 1,
            url: associatedUrl.url || "",
            summary: associatedUrl.summary || "",
          }),
        );

        const updatedData = {
          businessName: data.businessName || "",
          websiteUrl: url,
          businessDescription: data.businessDescription || "",
          companyLogo: data.companyLogo || "",
          companyColors: data.companyColors || [],
          companyTone: data.companyTone || "",
          stakeholders:
            stakeholdersWithIds.length > 0
              ? stakeholdersWithIds
              : [{ stakeholderId: 1, name: "", description: "" }],
          associatedUrls:
            associatedUrlsWithIds.length > 0
              ? associatedUrlsWithIds
              : [{ urlId: 1, url: "", summary: "" }],
        };

        setFormData(updatedData);
        onDataChange && onDataChange(transformDataForAPI(updatedData));
      } catch (err) {
        console.error("Failed to fetch business details:", err);
        setError(
          "Failed to load business details. You can still enter them manually.",
        );
        // Keep the URL if fetch fails
        setFormData((prev) => ({ ...prev, websiteUrl: url }));
      } finally {
        businessFetchStartedRef.current = false;
        dataReadyRef.current = true;
        if (loaderTimerDoneRef.current) {
          setLoading(false);
        }
      }
    };
    fetchPrelimBusinessDetails();
    fetchBusinessDetails();
  }, [url]);

  // Update URL if it changes
  useEffect(() => {
    if (url && !loading) {
      setFormData((prev) => ({ ...prev, websiteUrl: url }));
    }
  }, [url, loading]);

  // Transform formData to API format for onDataChange
  const transformDataForAPI = (data) => {
    const { associatedUrls, ...rest } = data;
    return {
      ...rest,
      associated_urls: associatedUrls.map(({ urlId, ...rest }) => rest),
    };
  };

  // ─── Edit lifecycle ────────────────────────────────────────────────────────

  const beginEdit = (key) => {
    setDraft({
      ...formData,
      companyColors: [...(formData.companyColors || [])],
      stakeholders: formData.stakeholders.map((s) => ({ ...s })),
      associatedUrls: formData.associatedUrls.map((u) => ({ ...u })),
    });
    setNewTone("");
    setEditing(key);
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraft(null);
    setNewTone("");
  };

  const saveEdit = () => {
    if (!draft) return;

    // A row the user emptied out is a row they meant to delete, but the shape
    // downstream always expects at least one entry in each list.
    const stakeholders = draft.stakeholders.filter(
      (s) => s.name.trim() || s.description.trim(),
    );
    const associatedUrls = draft.associatedUrls.filter(
      (u) => u.url.trim() || u.summary.trim(),
    );

    const committed = {
      ...draft,
      stakeholders: stakeholders.length
        ? stakeholders
        : [{ stakeholderId: 1, name: "", description: "" }],
      associatedUrls: associatedUrls.length
        ? associatedUrls
        : [{ urlId: 1, url: "", summary: "" }],
    };

    setFormData(committed);
    onDataChange && onDataChange(transformDataForAPI(committed));
    cancelEdit();
  };

  const patchDraft = (patch) => setDraft((prev) => ({ ...prev, ...patch }));

  // ↵ saves, Esc cancels — except inside a textarea, where ↵ is a newline.
  const handleEditorKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      cancelEdit();
      return;
    }
    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      event.target.tagName !== "TEXTAREA"
    ) {
      event.preventDefault();
      saveEdit();
    }
  };

  // ─── Tone helpers ──────────────────────────────────────────────────────────

  const draftTones = draft ? parseTone(draft.companyTone) : [];

  const setTones = (tones) =>
    patchDraft({ companyTone: tones.join(", ") });

  const addTone = (value) => {
    const tone = value.trim();
    if (!tone || draftTones.some((t) => t.toLowerCase() === tone.toLowerCase()))
      return;
    setTones([...draftTones, tone]);
    setNewTone("");
  };

  // ─── Stakeholder / URL list helpers (draft-only) ───────────────────────────

  const addStakeholder = () =>
    patchDraft({
      stakeholders: [
        ...draft.stakeholders,
        {
          stakeholderId:
            Math.max(0, ...draft.stakeholders.map((s) => s.stakeholderId)) + 1,
          name: "",
          description: "",
        },
      ],
    });

  const updateStakeholder = (stakeholderId, field, value) =>
    patchDraft({
      stakeholders: draft.stakeholders.map((s) =>
        s.stakeholderId === stakeholderId ? { ...s, [field]: value } : s,
      ),
    });

  const removeStakeholder = (stakeholderId) =>
    patchDraft({
      stakeholders: draft.stakeholders.filter(
        (s) => s.stakeholderId !== stakeholderId,
      ),
    });

  const addAssociatedUrl = () =>
    patchDraft({
      associatedUrls: [
        ...draft.associatedUrls,
        {
          urlId: Math.max(0, ...draft.associatedUrls.map((u) => u.urlId)) + 1,
          url: "",
          summary: "",
        },
      ],
    });

  const updateAssociatedUrl = (urlId, field, value) =>
    patchDraft({
      associatedUrls: draft.associatedUrls.map((u) =>
        u.urlId === urlId ? { ...u, [field]: value } : u,
      ),
    });

  const removeAssociatedUrl = (urlId) =>
    patchDraft({
      associatedUrls: draft.associatedUrls.filter((u) => u.urlId !== urlId),
    });

  // ─── Row content ───────────────────────────────────────────────────────────

  const logoTile = (size) => {
    const box =
      size === "lg" ? "h-11 w-11 rounded-[9px] text-xl" : "h-[22px] w-[22px] rounded-md text-[13px]";
    return formData.companyLogo ? (
      <img
        src={formData.companyLogo}
        alt={formData.businessName}
        className={clsx(box, "shrink-0 border border-gray-200 bg-gray-50 object-contain")}
      />
    ) : (
      <span
        className={clsx(
          box,
          "inline-flex shrink-0 items-center justify-center bg-primary/15 font-semibold text-primary",
        )}
      >
        {(formData.businessName || extractDomain(formData.websiteUrl) || "?")
          .charAt(0)
          .toUpperCase()}
      </span>
    );
  };

  const rows = [
    {
      key: "name",
      label: "Name & logo",
      summary: (
        <div className="mt-1 flex items-center gap-2 text-[13px] text-gray-900">
          {logoTile("sm")}
          {formData.businessName || <Empty>No name yet</Empty>}
          {formData.websiteUrl && (
            <span className="font-mono text-[11.5px] text-gray-400">
              {extractDomain(formData.websiteUrl)}
            </span>
          )}
        </div>
      ),
      editor: () => (
        <>
          <div className="mt-2.5 flex items-center gap-3">{logoTile("lg")}</div>
          <div className={clsx(labelClass, "mt-3.5 block")}>Company name</div>
          <input
            autoFocus
            type="text"
            value={draft.businessName}
            onChange={(e) => patchDraft({ businessName: e.target.value })}
            placeholder="Enter your business name"
            className={clsx(fieldClass, "mt-1.5")}
          />
          <div className={clsx(labelClass, "mt-3.5 block")}>Website</div>
          <input
            type="url"
            value={draft.websiteUrl}
            readOnly
            disabled
            className={clsx(
              fieldClass,
              "mt-1.5 cursor-not-allowed bg-gray-50 font-mono text-[12.5px] text-gray-500",
            )}
          />
        </>
      ),
    },
    {
      key: "desc",
      label: "What you do",
      summary: (
        <div className="mt-1 text-[13px] leading-[1.55] text-gray-900">
          {formData.businessDescription || (
            <Empty>Nothing captured — add a description</Empty>
          )}
        </div>
      ),
      editor: () => (
        <>
          <textarea
            autoFocus
            rows={4}
            value={draft.businessDescription}
            onChange={(e) => patchDraft({ businessDescription: e.target.value })}
            placeholder="Describe what your business does, your main products/services, and your target customers..."
            className={clsx(fieldClass, "mt-2.5 min-h-24 resize-y")}
          />
          <p className="mt-2 text-xs text-gray-400">
            This helps us understand your business model and create relevant
            engagement strategies.
          </p>
        </>
      ),
      hint: "⇧↵ for a new line",
    },
    {
      key: "colors",
      label: "Brand colours",
      summary: (formData.companyColors || []).length ? (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {formData.companyColors.map((color, idx) => (
            <span
              key={`${color}-${idx}`}
              className="h-[26px] w-[26px] rounded-[7px] border border-gray-200"
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
      ) : (
        <div className="mt-1">
          <Empty>No colours picked up</Empty>
        </div>
      ),
      editor: () => (
        <div className="mt-2.5 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {draft.companyColors.map((color, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2.5 rounded-lg border border-gray-200 bg-white p-1.5 pr-2"
            >
              <label
                className="relative block h-[26px] w-[26px] shrink-0 cursor-pointer overflow-hidden rounded-[7px] border border-gray-200"
                style={{ backgroundColor: color }}
              >
                <input
                  type="color"
                  value={/^#[0-9a-f]{6}$/i.test(color) ? color : "#000000"}
                  onChange={(e) => {
                    const updated = [...draft.companyColors];
                    updated[idx] = e.target.value;
                    patchDraft({ companyColors: updated });
                  }}
                  className="absolute h-0 w-0 opacity-0"
                />
              </label>
              <span className="flex-1 font-mono text-xs uppercase text-gray-900">
                {color}
              </span>
              {idx === 0 && (
                <span className="text-[10.5px] text-gray-400">Primary</span>
              )}
              <button
                type="button"
                aria-label="Remove colour"
                onClick={() =>
                  patchDraft({
                    companyColors: draft.companyColors.filter(
                      (_, i) => i !== idx,
                    ),
                  })
                }
                className="cursor-pointer text-gray-300 hover:text-gray-600"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              patchDraft({ companyColors: [...draft.companyColors, "#000000"] })
            }
            className="flex cursor-pointer items-center gap-2.5 rounded-lg border border-dashed border-gray-300 bg-white p-1.5 pr-2 text-left hover:border-gray-400"
          >
            <span className="h-[26px] w-[26px] shrink-0 rounded-[7px] border border-dashed border-gray-300" />
            <span className="font-mono text-xs text-gray-400">
              + Add a colour
            </span>
          </button>
        </div>
      ),
    },
    {
      key: "tone",
      label: "Tone of voice",
      summary: parseTone(formData.companyTone).length ? (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {parseTone(formData.companyTone).map((tone) => (
            <span
              key={tone}
              className="inline-flex h-[26px] items-center rounded-full bg-gray-100 px-2.5 text-xs font-medium text-gray-700"
            >
              {tone}
            </span>
          ))}
        </div>
      ) : (
        <div className="mt-1">
          <Empty>No tone captured</Empty>
        </div>
      ),
      editor: () => (
        <>
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5 rounded-lg border border-primary bg-white px-2.5 py-2 ring-[3px] ring-primary/15">
            {draftTones.map((tone) => (
              <span
                key={tone}
                className="inline-flex h-[26px] items-center gap-1.5 rounded-full bg-gray-100 px-2.5 text-xs font-medium text-gray-700"
              >
                {tone}
                <button
                  type="button"
                  aria-label={`Remove ${tone}`}
                  onClick={() =>
                    setTones(draftTones.filter((t) => t !== tone))
                  }
                  className="cursor-pointer text-[10px] text-gray-400 hover:text-gray-900"
                >
                  ✕
                </button>
              </span>
            ))}
            <input
              autoFocus
              value={newTone}
              onChange={(e) => setNewTone(e.target.value)}
              onKeyDown={(e) => {
                // ↵ commits the trait being typed; only an empty box saves the row.
                if ((e.key === "Enter" || e.key === ",") && newTone.trim()) {
                  e.preventDefault();
                  e.stopPropagation();
                  addTone(newTone);
                }
                if (e.key === "Backspace" && !newTone && draftTones.length) {
                  setTones(draftTones.slice(0, -1));
                }
              }}
              placeholder="Add a trait…"
              className="min-w-32 flex-1 border-0 bg-transparent p-0 text-[13px] text-gray-900 outline-none placeholder:text-gray-400 focus:ring-0"
            />
          </div>
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            <span className={labelClass}>Suggestions</span>
            {TONE_SUGGESTIONS.filter(
              (s) =>
                !draftTones.some((t) => t.toLowerCase() === s.toLowerCase()),
            )
              .slice(0, 4)
              .map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => addTone(suggestion)}
                  className="inline-flex h-[26px] cursor-pointer items-center rounded-full border border-dashed border-gray-300 bg-white px-2.5 text-xs text-gray-500 hover:border-primary hover:text-primary"
                >
                  + {suggestion}
                </button>
              ))}
          </div>
        </>
      ),
    },
    {
      key: "stakeholders",
      label: formData.businessName
        ? `Who are the stakeholders of ${formData.businessName}`
        : "Who are your stakeholders",
      summary: (
        <div className="mt-1 text-[13px] leading-[1.55] text-gray-900">
          {formData.stakeholders.filter((s) => s.name).length ? (
            formData.stakeholders
              .filter((s) => s.name)
              .map((s) => s.name)
              .join(" · ")
          ) : (
            <Empty>No stakeholders identified</Empty>
          )}
        </div>
      ),
      editor: () => (
        <div className="mt-2.5 flex flex-col gap-1.5">
          {draft.stakeholders.map((stakeholder, idx) => (
            <div
              key={stakeholder.stakeholderId}
              className="flex items-start gap-2.5 rounded-lg border border-gray-200 bg-white p-2"
            >
              <span
                className={clsx(
                  "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-[7px] text-[11px] font-semibold",
                  AVATAR_TONES[idx % AVATAR_TONES.length],
                )}
              >
                {initials(stakeholder.name)}
              </span>
              <div className="grid min-w-0 flex-1 gap-1.5 sm:grid-cols-[180px_1fr]">
                <input
                  autoFocus={idx === 0}
                  type="text"
                  value={stakeholder.name}
                  onChange={(e) =>
                    updateStakeholder(
                      stakeholder.stakeholderId,
                      "name",
                      e.target.value,
                    )
                  }
                  placeholder="Product designers"
                  className={clsx(fieldClass, "px-2.5 py-1.5 text-[12.5px] font-semibold")}
                />
                <input
                  type="text"
                  value={stakeholder.description}
                  onChange={(e) =>
                    updateStakeholder(
                      stakeholder.stakeholderId,
                      "description",
                      e.target.value,
                    )
                  }
                  placeholder="What they do with your product"
                  className={clsx(fieldClass, "px-2.5 py-1.5 text-[12.5px]")}
                />
              </div>
              <button
                type="button"
                aria-label="Remove stakeholder"
                onClick={() => removeStakeholder(stakeholder.stakeholderId)}
                className="mt-1.5 cursor-pointer text-gray-300 hover:text-gray-600"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <AddRow onClick={addStakeholder}>
            + Add another kind of person
          </AddRow>
        </div>
      ),
      hint: "At least one, with a description",
    },
    {
      key: "pages",
      label: "Pages I learned from",
      summary: (
        <div className="mt-1 font-mono text-xs leading-[1.6] text-gray-900">
          {formData.associatedUrls.filter((u) => u.url).length ? (
            formData.associatedUrls
              .filter((u) => u.url)
              .map((u) => {
                const { host, path } = splitUrl(u.url);
                return `${host}${path}`;
              })
              .join(" · ")
          ) : (
            <Empty>No extra pages read</Empty>
          )}
        </div>
      ),
      editor: () => (
        <div className="mt-2.5 flex flex-col gap-1.5">
          {draft.associatedUrls.map((associatedUrl, idx) => (
            <div
              key={associatedUrl.urlId}
              className="flex items-start gap-2.5 rounded-lg border border-gray-200 bg-white p-2"
            >
              <span className="mt-2 h-[15px] w-[15px] shrink-0 rounded bg-gradient-to-br from-primary-dark to-primary-light" />
              <div className="grid min-w-0 flex-1 gap-1.5">
                <input
                  autoFocus={idx === 0}
                  type="url"
                  value={associatedUrl.url}
                  onChange={(e) =>
                    updateAssociatedUrl(
                      associatedUrl.urlId,
                      "url",
                      e.target.value,
                    )
                  }
                  placeholder="https://example.com/pricing"
                  className={clsx(
                    fieldClass,
                    "px-2.5 py-1.5 font-mono text-[12px]",
                  )}
                />
                <input
                  type="text"
                  value={associatedUrl.summary}
                  onChange={(e) =>
                    updateAssociatedUrl(
                      associatedUrl.urlId,
                      "summary",
                      e.target.value,
                    )
                  }
                  placeholder="What this page covers, e.g. Pricing"
                  className={clsx(fieldClass, "px-2.5 py-1.5 text-[12.5px]")}
                />
              </div>
              <button
                type="button"
                aria-label="Remove page"
                onClick={() => removeAssociatedUrl(associatedUrl.urlId)}
                className="mt-1.5 cursor-pointer text-gray-300 hover:text-gray-600"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <AddRow onClick={addAssociatedUrl}>+ Add a page</AddRow>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="w-full flex items-start relative z-[100]">
        <MultiStepLoaderBusinessDetails
          loadingStates={[
            { text: "Getting your business name...", key: "companyName" },
            {
              text: "Gathering your business overview...",
              key: "companyDescription",
            },
            { text: "Finding your brand logo...", key: "companyLogo" },
            { text: "Extracting your brand colors...", key: "companyColors" },
            { text: "Analyzing your brand tone...", key: "companyTone" },
          ]}
          loading={loading}
          setLoading={handleLoaderComplete}
          duration={5000}
          businessInfo={prelimData}
          url={url}
        />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="relative"
    >
      <OnboardingGlow />

      <div
        className="relative mx-auto w-full max-w-[640px] px-5 pt-9"
        style={{ paddingBottom: "200px" }}
      >
        {/* Header — dims while a row is open, so the editor is the only thing live */}
        <div
          className={clsx(
            "text-center transition-opacity duration-200",
            editing && "opacity-45",
          )}
        >
          <div className="inline-flex h-7 items-center gap-2 rounded-full border border-gray-200 bg-white px-3 font-mono text-xs text-gray-500">
            <i className="h-1.5 w-1.5 rounded-full bg-primary" />
            AI Analysis Complete
          </div>
          <h1 className="mt-3.5 text-3xl font-semibold leading-tight tracking-[-0.02em] text-gray-900">
            Here's what I learned about{" "}
            <em
              className="font-normal not-italic text-primary"
              style={{
                fontFamily: "'Newsreader', Georgia, serif",
                fontStyle: "italic",
              }}
            >
              {formData.businessName || extractDomain(formData.websiteUrl)}
            </em>
          </h1>
          <p className="mx-auto mt-2 max-w-[560px] text-[15.5px] leading-[1.55] text-gray-500">
            Fluvo uses this to understand your customers, goals and voice & then
            builds your journeys and messages. Check anything you'd like to
            change below. You can update it later too.
          </p>
        </div>

        {/* Error banner */}
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-5 py-3.5"
          >
            <p className="text-amber-800 text-sm">{error}</p>
          </motion.div>
        )}

        {/* The findings, numbered. One row opens at a time; the rest recede. */}
        <div className="mt-3">
          {rows.map((row, index) => {
            const open = editing === row.key;
            const dimmed = editing !== null && !open;

            return (
              <div
                key={row.key}
                onKeyDown={open ? handleEditorKeyDown : undefined}
                className={clsx(
                  "grid grid-cols-[24px_minmax(0,1fr)_auto] items-start gap-3.5 border-t border-gray-100 py-3.5 transition-opacity duration-200",
                  dimmed && "opacity-40",
                  open &&
                    "-mx-4 my-1.5 rounded-xl border border-primary bg-white p-4 shadow-[0_0_0_3px_rgba(77,124,15,0.1),0_12px_34px_rgba(0,0,0,0.07)]",
                )}
              >
                <span
                  className={clsx(
                    "mt-px flex h-[22px] w-[22px] items-center justify-center rounded-full text-[11px] font-semibold",
                    open
                      ? "bg-primary text-white"
                      : "bg-primary/10 text-primary",
                  )}
                >
                  {index + 1}
                </span>

                <div className="min-w-0">
                  <RowLabel>{row.label}</RowLabel>
                  {open ? (
                    <>
                      {row.editor()}
                      <EditorActions
                        onSave={saveEdit}
                        onCancel={cancelEdit}
                        hint={row.hint || "Esc to cancel"}
                      />
                    </>
                  ) : (
                    row.summary
                  )}
                </div>

                {!open && (
                  <button
                    type="button"
                    aria-label={`Edit ${row.label}`}
                    disabled={editing !== null}
                    onClick={() => beginEdit(row.key)}
                    className="flex h-[26px] w-[26px] cursor-pointer items-center justify-center rounded-[7px] border border-transparent text-gray-400 hover:border-gray-200 hover:text-gray-900 disabled:cursor-default disabled:hover:border-transparent disabled:hover:text-gray-400"
                  >
                    <PencilIcon className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}
